/**
 * <anja-info-tabs>: product information panels as dropdowns (native <details>) or tabs.
 * Tabs mode builds a tablist from the panel titles; on phones it can stay as dropdowns.
 * Also: "Read more" for clamped descriptions and "Size guide" buttons that open the size guide.
 */
const mobile = matchMedia('(max-width: 749px)');

class AnjaInfoTabs extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.panels = /** @type {HTMLDetailsElement[]} */ (Array.from(this.querySelectorAll(':scope > details[data-anja-info-panel], :scope > .shopify-block > details[data-anja-info-panel]')));
    if (!this.panels.length) this.panels = /** @type {HTMLDetailsElement[]} */ (Array.from(this.querySelectorAll('details[data-anja-info-panel]')));

    if (this.dataset.firstOpen === 'true' && this.panels[0] && !this.panels.some((p) => p.open)) this.panels[0].open = true;

    this.#setupClamps();
    this.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {HTMLElement} */ (event.target).closest('[data-anja-open-size-guide]');
        if (!btn) return;
        /** @type {HTMLElement | null} */ (document.querySelector('.anja-size-guide__open'))?.click();
      },
      { signal }
    );

    if (this.dataset.style === 'tabs') {
      this.#sync();
      mobile.addEventListener('change', () => this.#sync(), { signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #setupClamps() {
    this.querySelectorAll('[data-anja-clamp]').forEach((clamp) => {
      const more = /** @type {HTMLButtonElement | null} */ (clamp.parentElement?.querySelector('[data-anja-more]'));
      if (!more) return;
      const label = more.textContent;
      const check = () => {
        if (clamp.classList.contains('is-open')) return;
        more.hidden = clamp.scrollHeight <= clamp.clientHeight + 2;
      };
      requestAnimationFrame(check);
      this.querySelectorAll('details').forEach((d) => d.addEventListener('toggle', check));
      more.addEventListener('click', () => {
        const open = clamp.classList.toggle('is-open');
        more.textContent = open ? more.dataset.less || label : label;
      });
    });
  }

  #sync() {
    const useTabs = !(mobile.matches && this.classList.contains('anja-info--mobile-accordion'));
    this.querySelector('.anja-info__tablist')?.remove();
    this.classList.toggle('is-tabs', useTabs);
    if (!useTabs) {
      this.panels.forEach((p) => p.removeAttribute('hidden'));
      return;
    }
    const list = document.createElement('div');
    list.className = 'anja-info__tablist';
    list.setAttribute('role', 'tablist');
    const select = (index, focus = false) => {
      this.panels.forEach((panel, i) => {
        panel.open = i === index;
        panel.hidden = i !== index;
        const tab = list.children[i];
        tab.setAttribute('aria-selected', String(i === index));
        tab.setAttribute('tabindex', i === index ? '0' : '-1');
        if (focus && i === index) /** @type {HTMLElement} */ (tab).focus();
      });
    };
    this.panels.forEach((panel, i) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'anja-info__tab';
      tab.setAttribute('role', 'tab');
      tab.textContent = panel.querySelector('[data-anja-info-title]')?.textContent || '';
      tab.addEventListener('click', () => select(i));
      tab.addEventListener('keydown', (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        select((i + step + this.panels.length) % this.panels.length, true);
      });
      list.append(tab);
    });
    this.prepend(list);
    select(Math.max(0, this.panels.findIndex((p) => p.open)));
  }
}

if (!customElements.get('anja-info-tabs')) customElements.define('anja-info-tabs', AnjaInfoTabs);
